import { FC, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';

const Navbar: FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  
  // Use solid background on non-home pages or when scrolled
  const useSolidBackground = !isHomePage || isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { name: 'Usługi', path: '/#services' },
    { name: 'O nas', path: '/#about' },
    { name: 'Realizacje', path: '/#projects' },
    { name: 'Transformacje', path: '/transformacje' },
    { name: 'Galeria', path: '/galeria' },
    { name: 'Kontakt', path: '/#contact' }
  ];
  
  return (
    <motion.nav 
      className={`fixed w-full z-50 transition-all duration-300 ${useSolidBackground ? 'bg-white shadow-md py-1' : 'bg-transparent py-2'}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center">
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/" className="flex items-center">
              <img 
                src={logo} 
                alt="ALUXIM Logo" 
                className="h-20 md:h-24" // Adjusted size from h-24 md:h-32
                style={{ objectFit: 'contain' }}
              />
            </Link>
          </motion.div>
          
          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item, i) => {
              const isAnchorLink = item.path.includes('#');
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 + i * 0.1 }}
                  whileHover={{ y: -2 }}
                >
                  {isAnchorLink && isHomePage ? (
                    <a 
                      href={item.path} 
                      className={`${useSolidBackground ? 'text-gray-800' : 'text-white'} hover:text-blue-600`}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`${useSolidBackground ? 'text-gray-800' : 'text-white'} hover:text-blue-600`}
                    >
                      {item.name}
                    </Link>
                  )}
                </motion.div>
              );
            })}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/#contact" 
                className={`${useSolidBackground ? 'bg-blue-700 text-white' : 'bg-white text-blue-900'} py-2 px-4 rounded-md font-medium hover:opacity-90 transition`}
              >
                Zapytaj o ofertę
              </Link>
            </motion.div>
          </div>
          
          {/* Mobile menu button */}
          <motion.div 
            className="md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <button
              type="button"
              className={`${useSolidBackground ? 'text-gray-800' : 'text-white'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </motion.div>
        </div>
        
        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="md:hidden bg-white mt-4 py-4 px-2 rounded-lg shadow-lg"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {menuItems.map((item, i) => {
                const isAnchorLink = item.path.includes('#');
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.1 }}
                  >
                    {isAnchorLink && isHomePage ? (
                      <a 
                        href={item.path}
                        className="block py-2 px-4 text-gray-800 hover:bg-gray-100 rounded"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.path}
                        className="block py-2 px-4 text-gray-800 hover:bg-gray-100 rounded"
                      >
                        {item.name}
                      </Link>
                    )}
                  </motion.div>
                );
              })}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.6 }}
              >
                <Link
                  to="/#contact"
                  className="block py-2 px-4 mt-2 bg-blue-700 text-white rounded text-center"
                >
                  Zapytaj o ofertę
                </Link>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
